import React from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import {
  Arrow,
  Calendar,
  CloseCircle,
  Currency,
  CurrencyBig,
  Lock,
  Warning,
} from '@/icons/index';

import Modal from '../Modal/Modal';
import Input from '../Input/Input';
import Select from '../Select/Select';
import Button from '../Button/Button';
import InformationModal from '../InformationModal/InformationModal';
import TextBlock from '../TextBlock/TextBlock';

import styles from './TopUpBalanceModal.module.scss';

const strategy = {
  chooseMethod: 'chooseMethod',
  onlineTopUp: 'onlineTopUp',
  cinKredit: 'cinKredit',
  klassKredit: 'klassKredit',
  moneyTransfer: 'moneyTransfer',
};

const paymentMethodData = [
  {
    name: 'Banking card',
    value: 'banking-card',
  },
  {
    name: 'VAT transfer',
    value: 'vat-transfer',
  },
];

const amountData = [
  {
    amount: 1,
  },
  {
    amount: 2,
  },
  {
    amount: 3,
  },
];

const TopUpBalanceModal = ({ active, setActive, onLogin, user }) => {
  const route = useRouter();

  const [flowStage, setFlowStage] = React.useState(strategy.chooseMethod);
  const [paymentMethod, setPaymentMethodData] = React.useState(
    paymentMethodData[0],
  );
  const [successModal, setSuccessModal] = React.useState(false);
  const [errorModal, setErrorModal] = React.useState(false);
  const [selectAmount, setSelectAmount] = React.useState(amountData[1]);
  const [isBusiness, setIsBusiness] = React.useState(false);

  React.useEffect(() => {
    const isBusinessPage = route.pathname.startsWith('/business');
    setIsBusiness(isBusinessPage);
  }, [route.pathname]);

  const onApply = () => {
    setActive(false);
    setSuccessModal(true);
    setFlowStage(strategy.chooseMethod);
  };

  const onError = () => {
    setActive(false);
    setErrorModal(true);
    setFlowStage(strategy.chooseMethod);
  };

  const selectType = (type) => {
    return () => {
      setFlowStage(type);
    };
  };

  const onClose = (value) => {
    setActive(value);
    setTimeout(() => {
      setFlowStage(strategy.chooseMethod);
    }, 300);
  };

  const renderAmount = (item, index) => {
    return (
      <button
        aria-label={item.amount}
        key={index}
        onClick={() => setSelectAmount(item)}
        className={classNames([
          styles.amount,
          { [styles.amountSelect]: item.amount === selectAmount.amount },
        ])}
      >
        {item.amount} <CurrencyBig />
      </button>
    );
  };

  return (
    <>
      <Modal
        active={active}
        setActive={onClose}
        classNameInner={styles.modalInner}
        itemScope
        itemType='http://schema.org/Property'
      >
        <div className={styles.modalContent}>
          {flowStage === strategy.chooseMethod && (
            <>
              <div className={'title'}>Top-up your balance</div>
              <div
                className={classNames([
                  styles.content,
                  { [styles.contentUser]: !!user },
                ])}
              >
                <Button
                  variant={'outline'}
                  onClick={() => setFlowStage(strategy.onlineTopUp)}
                >
                  Online top-up
                </Button>
                {!user && (
                  <div className={styles.separation}>
                    <span className={styles.text}>
                      Available after
                      <button
                        aria-label='Sign In'
                        className={styles.button}
                        onClick={onLogin}
                      >
                        Sign In
                      </button>
                    </span>
                  </div>
                )}
                <div className={styles.actions}>
                  {!isBusiness && (
                    <>
                      <Button
                        variant={user ? 'outline' : 'blocked'}
                        className={styles.action}
                        onClick={
                          user ? selectType(strategy.cinKredit) : onLogin
                        }
                      >
                        {!user && <Lock />}CINKredit
                      </Button>
                      <Button
                        variant={user ? 'outline' : 'blocked'}
                        className={styles.action}
                        onClick={
                          user ? selectType(strategy.klassKredit) : onLogin
                        }
                      >
                        {!user && <Lock />}KlassKredit
                      </Button>
                    </>
                  )}
                  <Button
                    variant={user ? 'outline' : 'blocked'}
                    className={styles.action}
                    onClick={
                      user ? selectType(strategy.moneyTransfer) : onLogin
                    }
                  >
                    {!user && <Lock />}Money transfer
                  </Button>
                </div>
              </div>
            </>
          )}
          {flowStage === strategy.onlineTopUp && (
            <>
              <div>
                <button
                  aria-label='Back'
                  onClick={() => setFlowStage(strategy.chooseMethod)}
                  className={styles.goBack}
                >
                  <Arrow width={22} /> Back
                </button>
              </div>
              <div className={'title'}>Online top-up</div>
              <div className={styles.contentOnlinePayment}>
                <div className={styles.first}>
                  <Input
                    className={styles.phone}
                    label={'Phone number'}
                    placeholderMask={'+994'}
                    placeholder={'+994 (XXX) XXX XX-XX'}
                    mask={'+\\9\\9\\4-999-999-99-99'}
                    type='tel'
                  />
                  <Input label={'Amount'} placeholder={'5 AZN'} />
                </div>
                <div className={styles.second}>
                  <Select
                    value={paymentMethod}
                    data={paymentMethodData}
                    label={'Payment method'}
                    onChange={setPaymentMethodData}
                  />
                </div>
                <div className={styles.third}>
                  <Input
                    label={'Card number'}
                    placeholder={'XXXX XXXX XXXX XXXX'}
                    mask={'9999 9999 9999 9999'}
                    type='number'
                  />
                  <Input
                    label={'Expiry date'}
                    placeholder={'MM/YY'}
                    mask={'99/99'}
                    IconRight={Calendar}
                  />
                  <Input label={'CVC/CVV'} placeholder={'•••'} mask={'999'} />
                </div>
                <div>
                  <Button onClick={onApply}>Pay now</Button>
                </div>
              </div>
            </>
          )}
          {flowStage === strategy.cinKredit && (
            <>
              <div>
                <button
                  aria-label='Back'
                  onClick={() => setFlowStage(strategy.chooseMethod)}
                  className={styles.goBack}
                >
                  <Arrow width={22} /> Back
                </button>
              </div>
              <div className={'title'}>CinKredit</div>
              <div className={styles.contentCinKredit}>
                <div className={styles.banner}>
                  <span className={styles.price}>
                    1 <Currency />
                  </span>
                  <TextBlock className={styles.text}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                    Vivamus et sagittis neque, magna quis mattis diam in
                    aliquam.
                  </TextBlock>
                </div>
                <div className={styles.warning}>
                  <div className={styles.icon}>
                    <Warning />
                  </div>
                  <span className={styles.text}>
                    Neque porro quisquam est, qui dolorem ipsum quia dolor sit
                    amet, consectetur, adipisci velit, sed quia non Nemo enim{' '}
                    <span className={styles.red}>fee 0.2₼</span> voluptatem quia
                    voluptas sit
                  </span>
                </div>
                <div>
                  <Button onClick={onError}>Get it now</Button>
                </div>
              </div>
            </>
          )}
          {flowStage === strategy.klassKredit && (
            <>
              <div>
                <button
                  aria-label='Back'
                  onClick={() => setFlowStage(strategy.chooseMethod)}
                  className={styles.goBack}
                >
                  <Arrow width={22} /> Back
                </button>
              </div>
              <div className={'title'}>KlassKredit</div>
              <div className={styles.klassKreditInfo}>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus
                et sagittis neque, magna quis mattis diam in aliquam.
              </div>
              <div className={styles.contentKlassKreditInfo}>
                <span className={styles.amountTitle}>Amount</span>
                <div className={styles.amounts}>
                  {amountData.map(renderAmount)}
                </div>
                <div className={styles.warning}>
                  <div className={styles.icon}>
                    <Warning />
                  </div>
                  <span className={styles.text}>
                    Neque porro quisquam est, qui dolorem ipsum quia dolor sit
                    amet, consectetur, adipisci velit, sed quia non Nemo enim{' '}
                    <span className={styles.red}>fee 0.2₼</span> voluptatem quia
                    voluptas sit
                  </span>
                </div>
                <div>
                  <Button onClick={onApply}>Get it now</Button>
                </div>
              </div>
            </>
          )}
          {flowStage === strategy.moneyTransfer && (
            <>
              <div>
                <button
                  onClick={() => setFlowStage(strategy.chooseMethod)}
                  className={styles.goBack}
                >
                  <Arrow width={22} /> Back
                </button>
              </div>
              <div className={'title'}>Money transfer</div>
              <div className={styles.contentKlassKreditInfo}>
                <div className={styles.contentOnlinePayment}>
                  <div className={styles.first}>
                    <Input
                      className={styles.phone}
                      label={'Phone number'}
                      placeholderMask={'+994'}
                      placeholder={'+994 (XXX) XXX XX-XX'}
                      mask={'+\\9\\9\\4-999-999-99-99'}
                      type='tel'
                    />
                  </div>
                </div>
                <div className={styles.banner}>
                  <span className={styles.price}>
                    0.2 <Currency />
                  </span>
                  <TextBlock className={styles.text}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                    Vivamus et sagittis neque, magna quis mattis diam in
                    aliquam.
                  </TextBlock>
                </div>
                <div className={styles.warning}>
                  <div className={styles.icon}>
                    <Warning />
                  </div>
                  <span className={styles.text}>
                    Neque porro quisquam est, qui dolorem ipsum quia dolor sit
                    amet, consectetur, adipisci velit, sed quia non Nemo enim{' '}
                    <span className={styles.red}>fee 0.2₼</span> voluptatem quia
                    voluptas sit
                  </span>
                </div>
                <div>
                  <Button onClick={onApply}>Submit</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
      <InformationModal
        onClose={() => setErrorModal(false)}
        active={errorModal}
        title={'Error'}
        description={"You don't have enough money to pay the fee"}
        Icon={CloseCircle}
      />
      <InformationModal
        title={'Success'}
        description={'Online payment was successfully received'}
        active={successModal}
        onClose={() => setSuccessModal(false)}
      />
    </>
  );
};

export default TopUpBalanceModal;
